import { Routes, Route, Navigate } from "react-router-dom";
import { RequireAuth } from "./auth/session.js";
import { Login } from "./auth/Login.js";
import { AuthCallback } from "./auth/AuthCallback.js";
import { Shell } from "./shell/Shell.js";
import { Portfolio } from "./routes/Portfolio.js";
import { Project } from "./routes/Project.js";
import { Evidence } from "./routes/Evidence.js";
import { ComingSoon } from "./routes/ComingSoon.js";
import { ReviewQueue } from "./routes/ReviewQueue.js";
import { Connectors } from "./routes/Connectors.js";
import { Assessment } from "./routes/Assessment.js";
import { Roadmap } from "./routes/Roadmap.js";
import { Calendar } from "./routes/Calendar.js";
import { OpsView } from "./routes/OpsView.js";
import { AdminPortfolio } from "./routes/AdminPortfolio.js";

/**
 * The route table. /login and /auth/callback (the IdP handoff landing) are public; everything
 * else is behind <RequireAuth> inside the <Shell>
 * frame (top bar + footer). Every destination in the primary nav is now a real screen: Portfolio,
 * Review queue and (as of NX-2640) Connectors. ComingSoon survives only as the catch-all's honest
 * "not found" state, not as a promise about a later milestone. Wrap this in <SessionProvider> + a
 * Router (see main.tsx; tests use MemoryRouter).
 */
export function App(): React.ReactNode {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/portfolio" replace />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/projects/:id" element={<Project />} />
        <Route path="/work-items/:id/evidence" element={<Evidence />} />
        <Route path="/review-queue" element={<ReviewQueue />} />
        {/* NX-2645: /forecast, /capability, /reliability and /friction are gone — the locked design
            has no such screens (design/08 §1 makes them Project-detail panels; §3's inventory omits
            them). A bookmark now falls through to the catch-all rather than to a stub promising a
            screen that was never designed. NX-2640 then replaced the last stub in primary nav,
            /connectors, with the real E8.11 surface — ComingSoon now serves only the catch-all. */}
        <Route path="/connectors" element={<Connectors />} />
        {/* E8.10 (NX-2638). Two paths, one screen: /assessment carries its own project picker so a
            primary-nav entry can reach it, and /projects/:id/assessment stays deep-linkable with
            the project preselected. */}
        <Route path="/assessment" element={<Assessment />} />
        <Route path="/projects/:id/assessment" element={<Assessment />} />
        {/* E8.14 (NX-2643) — Timeline. A DELIBERATE re-add of a nav destination of the kind
            NX-2645 (above) retired, per Papa's 2026-08-21 ruling: the plan wins here. See the
            matching comment on TopBar.tsx's NAV entry. */}
        <Route path="/timeline" element={<Roadmap />} />
        {/* NX-2745 (E8.15) — org-wide delivery calendar: month grid over the roadmap read-model. */}
        <Route path="/calendar" element={<Calendar />} />
        {/* NX-2737 (E12.3) — internal admin-only ops console; API 403s every other role. */}
        <Route path="/ops" element={<OpsView />} />
        {/* NX-2855 — admin-only declared portfolio management (program/project/profile/reparent). */}
        <Route path="/admin/portfolio" element={<AdminPortfolio />} />
        <Route path="*" element={<ComingSoon title="Not found" />} />
      </Route>
    </Routes>
  );
}
